import { BeatResolution, Key, Scale } from '@arpeggi/shared'
import { Instance, types } from 'mobx-state-tree'
import { ForDirectExtend } from '../../../../features/mobx'
import { InstrumentPart, Octave } from '../../../client/genesis/genesis_proto_pb'
import { Arp2Utils } from '../../../client/SongUtils'
import { LoopSettingsStore } from './LoopSettingsStore'
import { MelodicLoopSettingsStore } from './MelodicLoopSettingsStore'
import { NoteCollectionStore } from './NoteCollectionStore'

/**
 * Represents a single Melodic Loop (basically, a piano roll composition).
 */
const _MelodicLoopStore = types
  .model({
    settings: types.optional(LoopSettingsStore, {}),
    melodicSettings: types.optional(MelodicLoopSettingsStore, {}),
    notes: types.optional(NoteCollectionStore, {})
  })
  .views((self) => ({
    get lengthOfLoop() {
      return Arp2Utils.getLengthOfRes(self.settings.resolution)
    }
  }))
  .actions((self) => {
    /** Sets this loop store to empty. */
    function initialize(resolution: BeatResolution) {
      self.settings = LoopSettingsStore.create({ resolution: resolution })
      self.melodicSettings = MelodicLoopSettingsStore.create()
      self.notes = NoteCollectionStore.create()
    }
    function clearNotes() {
      self.notes.clearNotes()
    }
    function updateResolution(resolution: BeatResolution) {
      self.settings.resolution = resolution
    }
    function updateScale(scale: Scale) {
      self.melodicSettings.scale = scale
      self.notes.updateScale(scale, self.melodicSettings.key)
    }

    // Note: you can only change key when in Major/Minor mode, so just transpose all notes.
    function updateKey(key: Key) {
      let keyOffset = key - self.melodicSettings.key
      // Put KeyOffset in range [-6, 6] so we don't shift things too much.
      if (keyOffset > 6) keyOffset -= 12
      if (keyOffset < -6) keyOffset += 12
      transposeBy(keyOffset)
      self.melodicSettings.key = key
    }

    // Positive offset means transposing up in pitch.
    function transposeBy(offset: number) {
      self.notes.transposeBy(offset)
    }

    /** Converts empty noteRows into null for efficiency.  */
    function minimizeForStorage() {
      self.notes.minimizeForStorage()
    }

    function populateFromNumberArray(
      numberArr: number[],
      scale: Scale = Scale.CHROMATIC,
      octave = Octave.ZERO,
      key: Key = Key.C
    ) {
      self.melodicSettings = MelodicLoopSettingsStore.create()
      self.melodicSettings.scale = scale
      self.notes.clearNotes()
      self.notes.populateFromNumberArray(numberArr, octave, key)
    }

    function fromGenesis(
      part: InstrumentPart,
      resolution: BeatResolution,
      octave: Octave,
      genesisKey: Key
    ) {
      self.settings = LoopSettingsStore.create({ resolution: resolution })
      populateFromNumberArray(part.getNotesList(), Scale.CHROMATIC, octave, genesisKey)
    }

    return {
      initialize,
      clearNotes,
      updateResolution,
      updateScale,
      updateKey,
      minimizeForStorage,
      fromGenesis,
      transposeBy,
      populateFromNumberArray
    }
  })

interface MelodicLoopStoreType extends ForDirectExtend<typeof _MelodicLoopStore> {}
export const MelodicLoopStore: MelodicLoopStoreType = _MelodicLoopStore

export interface IMelodicLoopStore extends ForDirectExtend<Instance<typeof MelodicLoopStore>> {}
